import { initializeApp } from "firebase/app";
import { getAuth, GoogleAuthProvider } from "firebase/auth";
import { getFirestore, doc, getDocFromServer, initializeFirestore, enableMultiTabIndexedDbPersistence } from "firebase/firestore";
import firebaseConfig from "../firebase-applet-config.json";

const app = initializeApp(firebaseConfig);
export const auth = getAuth(app);
export const googleProvider = new GoogleAuthProvider();

// Use initializeFirestore with force long polling to bypass potential gRPC/Stream issues in cloud containers
const firestoreDatabaseId = (firebaseConfig as any).firestoreDatabaseId;
export const db = initializeFirestore(app, {
  experimentalForceLongPolling: true,
  ignoreUndefinedProperties: true,
}, firestoreDatabaseId && firestoreDatabaseId !== "(default)" ? firestoreDatabaseId : "(default)");

// Enable offline persistence for better efficiency on slow networks
if (typeof window !== 'undefined') {
  enableMultiTabIndexedDbPersistence(db).catch((err) => {
    if (err.code === 'failed-precondition') {
      console.warn('Firestore persistence failed: Multiple tabs open');
    } else if (err.code === 'unimplemented') {
      console.warn('Firestore persistence failed: Browser not supported');
    } else {
      console.warn('Firestore persistence error:', err);
    }
  });
}

console.log("Firebase initialized with project:", firebaseConfig.projectId);
console.log("Firestore using database:", (firebaseConfig as any).firestoreDatabaseId || "(default)");

export let analytics: any = null;

if (typeof window !== 'undefined' && firebaseConfig.measurementId && firebaseConfig.measurementId.trim() !== "") {
  import("firebase/analytics").then(({ getAnalytics, isSupported }) => {
    isSupported().then((supported) => {
      if (supported) {
        try {
          analytics = getAnalytics(app);
        } catch (e) {
          console.warn("Firebase Analytics initialization failed:", e);
        }
      }
    }).catch(err => console.warn("Firebase Analytics not supported:", err));
  }).catch(err => console.warn("Failed to load Firebase Analytics module:", err));
}

export enum OperationType {
  CREATE = 'create',
  UPDATE = 'update',
  DELETE = 'delete',
  LIST = 'list',
  GET = 'get',
  WRITE = 'write',
}

export function handleFirestoreError(error: unknown, operationType: OperationType, path: string | null) {
  const errorMsg = error instanceof Error ? error.message : String(error);
  const errorCode = (error as any)?.code;
  
  const isNetworkError = 
    errorCode === 'unavailable' || 
    errorCode === 'deadline-exceeded' ||
    errorMsg.toLowerCase().includes('offline') || 
    errorMsg.toLowerCase().includes('could not reach') || 
    errorMsg.toLowerCase().includes('connection failed') ||
    errorMsg.toLowerCase().includes('unavailable') ||
    errorMsg.toLowerCase().includes('timeout');

  const errInfo = {
    error: errorMsg,
    code: errorCode,
    authInfo: {
      userId: auth.currentUser?.uid,
      email: auth.currentUser?.email,
      emailVerified: auth.currentUser?.emailVerified,
      isAnonymous: auth.currentUser?.isAnonymous,
      tenantId: auth.currentUser?.tenantId,
      providerInfo: auth.currentUser?.providerData.map(provider => ({
        providerId: provider.providerId,
        displayName: provider.displayName,
        email: provider.email,
        photoUrl: provider.photoURL
      })) || []
    },
    operationType,
    path
  };

  if (isNetworkError) {
    // Return gracefully for network errors, the app should use cached data
    console.debug('Firestore Connectivity Notice (Running in Offline Mode):', errorMsg);
    return;
  }

  console.error('Firestore Error: ', JSON.stringify(errInfo));
  throw new Error(JSON.stringify(errInfo));
}

// Validate connection to Firestore - silent on failure to avoid log noise
async function testConnection() {
  try {
    await getDocFromServer(doc(db, 'test', 'connection'));
  } catch (error) {
    // Silently ignore startup connection checks
  }
}
testConnection();
